import { useEffect } from 'react';
import { useQuote } from '../../contexts/QuoteContext';
import { EditableField } from '../../components/EditableField/index';
import { WorkflowEditor } from '../../components/WorkflowEditor/index';
import { TabContainer } from '../../components/TabContainer/index';

interface QuoteModalProps {
  quote: any;
  onClose: () => void;
}

export const QuoteModal = ({ quote, onClose }: QuoteModalProps) => {
  const { 
    setCurrentQuote, 
    setCurrentWorkflow, 
    loadWorkflow, 
    isLoadingWorkflow,
    updateQuoteField,
    updateLineItem,
    addLineItem,
    removeLineItem,
    saveQuote,
    deleteQuote,
    isSaving
  } = useQuote();

  useEffect(() => {
    // Set the current quote in context
    setCurrentQuote(quote);
    
    // Load workflow if it exists
    if (quote.workflowId) {
      loadWorkflow(quote.workflowId);
    }
    
    // Cleanup when modal closes
    return () => {
      setCurrentQuote(null);
      setCurrentWorkflow(null);
    };
  }, [quote]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>{quote.title}</h2>
          <button onClick={onClose} className="close-button">✕</button>
        </div>
        
        <div className="modal-body">
          <TabContainer
            tabs={[
              {
                id: 'quote',
                label: '📝 Edit Quote',
                content: (
                  <div className="quote-tab">
                    <div className="quote-details">
                      <div className="detail-row">
                        <span className="label">Customer:</span>
                        <EditableField
                          value={quote.customer || ''}
                          onSave={(value) => updateQuoteField('customer', value)}
                          placeholder="Enter customer name"
                        />
                      </div>
                      <div className="detail-row">
                        <span className="label">Total Price:</span>
                        <span className="value price calculated-price">
                          ${(quote.totalPrice || 0).toLocaleString()}
                        </span>
                        <span className="auto-calc-label">(Auto-calculated)</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Status:</span>
                        <span className="value">
                          {quote.approved ? (
                            <span className="status-indicator approved">✅ Approved</span>
                          ) : (
                            <span className="status-indicator pending">📋 Pending Approval</span>
                          )}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Created:</span>
                        <span className="value">{new Date(quote.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="line-items-section">
                      <div className="section-header">
                        <h3>Line Items</h3>
                        <button onClick={addLineItem} className="add-line-item-button">
                          ➕ Add Item
                        </button>
                      </div>
                      
                      {quote.lineItems && quote.lineItems.length > 0 ? (
                        <div className="editable-line-items">
                          {quote.lineItems.map((item: any, index: number) => (
                            <div key={index} className="editable-line-item">
                              <div className="line-item-fields">
                                <div className="field-group">
                                  <label>Description</label>
                                  <EditableField
                                    value={item.description || ''}
                                    onSave={(value) => updateLineItem(index, 'description', value)}
                                    placeholder="Item description"
                                    className="description-field"
                                  />
                                </div>
                                <div className="field-group">
                                  <label>Price</label>
                                  <EditableField
                                    value={item.price || 0}
                                    onSave={(value) => updateLineItem(index, 'price', value)}
                                    type="number"
                                    prefix="$"
                                    placeholder="0"
                                    className="price-field"
                                  />
                                </div>
                                <div className="field-group">
                                  <label>Quantity</label>
                                  <EditableField
                                    value={item.quantity || 1}
                                    onSave={(value) => updateLineItem(index, 'quantity', value)}
                                    type="number"
                                    placeholder="1"
                                    className="quantity-field"
                                  />
                                </div>
                                <div className="field-group">
                                  <label>Discount %</label>
                                  <EditableField
                                    value={item.discount || 0}
                                    onSave={(value) => updateLineItem(index, 'discount', value)}
                                    type="number"
                                    placeholder="0"
                                    className="discount-field"
                                  />
                                </div>
                              </div>
                              <button 
                                onClick={() => removeLineItem(index)}
                                className="remove-line-item-button"
                                title="Remove this item"
                              >
                                🗑️
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-line-items">
                          <p>No line items yet</p>
                          <button onClick={addLineItem} className="add-first-item-button">
                            Add First Item
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="modal-actions">
                      <button 
                        onClick={async () => {
                          if (window.confirm('Are you sure you want to delete this quote? This action cannot be undone.')) {
                            try {
                              await deleteQuote();
                              onClose(); // Close modal after deletion
                              window.location.reload(); // Refresh to update quotes list
                            } catch (error) {
                              alert('Failed to delete quote: ' + error);
                            }
                          }
                        }}
                        className="delete-quote-button"
                        disabled={isSaving}
                      >
                        🗑️ Delete Quote
                      </button>
                      <button 
                        onClick={saveQuote} 
                        className="save-quote-button"
                        disabled={isSaving}
                      >
                        {isSaving ? '💾 Saving...' : '💾 Save Quote'}
                      </button>
                    </div>
                  </div>
                )
              },
              {
                id: 'workflow',
                label: '🔄 Workflow',
                content: (
                  <div className="workflow-tab">
                    {quote.workflowId ? (
                      isLoadingWorkflow ? (
                        <p>Loading workflow...</p>
                      ) : (
                        <WorkflowEditor />
                      )
                    ) : (
                      <div className="no-workflow">
                        <p>No workflow associated with this quote.</p>
                      </div>
                    )}
                  </div>
                )
              }
            ]}
            defaultTab="quote"
          />
        </div>
      </div>
    </div>
  );
};